import { createEffect, type JSX } from "solid-js";
import { X } from "@fleet/icons";
import { cn } from "@space/shared";

import { useNavDrawerSwipe } from "@/features/layout/hooks/use-nav-drawer-swipe";

/**
 * Panel transform. While open and at rest it returns exactly
 * `translate3d(0, 0, 0)`: drag only shows up in the style when there's
 * drag, so rest stays a single, comparable value.
 */
function transformDelPanel(abierto: boolean, arrastrePx: number): string {
  if (!abierto) {
    return "translate3d(-100%, 0, 0)";
  }
  return arrastrePx === 0 ? "translate3d(0, 0, 0)" : `translate3d(${arrastrePx}px, 0, 0)`;
}

/**
 * Mobile navigation drawer, with the same mechanism as the canon
 * (`listto-app` → `features/layout/components/app-layout-sidebar.tsx`).
 *
 * The key piece is that it's **always mounted** and enters and exits by
 * sliding with `transform`, instead of mounting and unmounting. A node that
 * disappears from the DOM can't animate its exit: it vanishes in the same
 * frame the condition stops holding. Keeping it mounted makes the close visible.
 *
 * Only `transform` (the drawer) and `opacity` (the backdrop) are animated,
 * which are the two properties the compositor resolves without recomputing
 * layout or repainting.
 *
 * It lives apart from the shell for two reasons: it's exactly the separation
 * the canon already has, and it can be tested without spinning up session,
 * router, i18n and PowerSync — which is what leaves a layout component
 * without tests.
 */
export function MobileNavDrawer(props: {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
  /** Accessible label for the close button (already translated). */
  closeLabel: string;
  children: JSX.Element;
}) {
  let drawerBox: HTMLDivElement | undefined;
  let panel: HTMLElement | undefined;
  // `inert` is set as an ATTRIBUTE by hand, not as a prop. Solid would assign
  // it as a property of the element, and a property isn't reflected in the
  // DOM: the guard would work in the browser but be invisible to any test —
  // green without evidence, which is exactly what we don't want in an
  // accessibility guard.
  createEffect(() => {
    drawerBox?.toggleAttribute("inert", !props.isOpen());
  });

  // Swiping left closes it; swiping right, nothing (see the hook).
  // The gesture listens on the BOX, not the panel, so it also works when
  // dragging from the darkened backdrop.
  const swipe = useNavDrawerSwipe({
    isOpen: () => props.isOpen(),
    close: () => props.setOpen(false),
    panel: () => panel,
  });

  return (
    <div
      ref={(el) => {
        drawerBox = el;
        swipe.attach(el);
      }}
      data-testid="mobile-nav-drawer"
      class={cn(
        // `touch-pan-y`: the browser keeps vertical scroll and hands us the
        // horizontal axis. Without this it competes with the gesture and the drag breaks.
        "fixed inset-0 z-40 overflow-hidden touch-pan-y md:hidden transition-all duration-300 ease-swift",
        props.isOpen() ? "pointer-events-auto" : "pointer-events-none",
      )}
      // By staying mounted, its navigation would still be in the tab order
      // and in the accessibility tree with the drawer closed: invisible to
      // whoever's looking, but reachable by keyboard and announced by a
      // screen reader. `pointer-events-none` only stops the pointer, not
      // that. `inert` does, and it's the trade-off for not unmounting.
    >
      <button
        type="button"
        data-testid="mobile-nav-backdrop"
        onClick={() => props.setOpen(false)}
        class={cn(
          "absolute inset-0 h-full w-full cursor-default border-none bg-black/40 p-0 backdrop-blur-[2px] ease-swift",
          // While the finger is in control, the backdrop obeys the finger:
          // the transition turns off and the drag sets the opacity.
          swipe.isDragging() ? "transition-none" : "transition-opacity duration-300",
          props.isOpen() ? "opacity-100" : "opacity-0",
        )}
        style={{ opacity: swipe.isDragging() ? String(swipe.backdropOpacity()) : undefined }}
        aria-label={props.closeLabel}
      />
      <aside
        ref={panel}
        data-testid="mobile-nav-aside"
        class={cn(
          "absolute bottom-0 left-0 top-0 h-full w-64 border-r border-border bg-background shadow-2xl ease-swift will-change-transform",
          // With a transition, the panel would arrive 300ms late to where the finger is.
          swipe.isDragging() ? "transition-none" : "transition-transform duration-300",
        )}
        style={{
          transform: transformDelPanel(props.isOpen(), swipe.dragPx()),
        }}
      >
        <button
          type="button"
          class="absolute right-2 top-2 text-muted-foreground"
          onClick={() => props.setOpen(false)}
          aria-label={props.closeLabel}
        >
          <X class="h-5 w-5" />
        </button>
        {props.children}
      </aside>
    </div>
  );
}
